/**
 * The prop contract for the reports screen (`/reports`).
 *
 * Every shape below mirrors a serializer class in
 * `app/serializers/staff/report_serializer.rb`, which is the authority. That one
 * hash is also what `Staff::ReportCsvService` renders, so the export cannot
 * disagree with the screen.
 *
 * **Money is `string`, never `number`, everywhere in this file.** Every amount
 * arrives as an exact decimal string that `Staff::ReportSerializer.money(cents)`
 * produced from integer cents; this screen formats those strings and never adds,
 * subtracts or scales them. The rule is the one `features/staff/till.ts` states
 * for the Z report, and it bites harder here: R1's binding criterion is that the
 * period total equals the sum of the Z reports inside it TO THE CENTAVO, and a
 * figure that has been through an IEEE-754 double on its way to the screen
 * cannot be held to that.
 *
 * Ratios (`growthRatio`, `share`, `cumulativeShare`) are the one exception and
 * are deliberately `number`: they are already rounded to four places server-side
 * and render as percentages, which is a display operation. No peso figure is
 * ever derived from one.
 *
 * Labels (`reasonLabel`, `paymentMethodLabel`, an actor's `label`) are resolved
 * server-side through the policy classes. A second copy of a translated enum
 * label in TSX is a second copy that goes stale.
 */

/** `Staff::ReportPeriod::PRESETS`. */
export const REPORT_PRESETS = [
  "today",
  "yesterday",
  "week",
  "month",
  "last_90_days",
  "quarter",
  "last_30_shifts",
] as const

export type ReportPreset = (typeof REPORT_PRESETS)[number]

/**
 * Not a member of `REPORT_PRESETS`, deliberately, and it never reaches the
 * server as a bare `?period=custom` on its own — see
 * `Staff::ReportPeriod::CUSTOM`'s header. It exists here only so the picker
 * and the "Personalizado" chip have one shared literal to compare `preset`
 * against, rather than each hand-rolling the string.
 */
export const CUSTOM_REPORT_PRESET = "custom" as const

/** `Staff::ReportPeriod::DEFAULT_PRESET`. The server falls back to this too. */
export const DEFAULT_REPORT_PRESET: ReportPreset = "month"

/** One chip, or one row inside the custom-range drawer. The label is `ui.staff.reports.presets.*`, resolved server-side. */
export type ReportPresetOption = {
  value: ReportPreset
  label: string
}

/**
 * `Staff::ReportSerializer::PeriodSerializer`.
 *
 * `label` is the SHIFT SPAN, not the calendar month — "Del cierre del 31 jul
 * 02:10 al cierre del 31 ago 01:45 · 31 turnos". The browser must not restate
 * it: money settling after the last close lands in the next shift's Z, so a
 * client-built "agosto" would promise a range the figures do not cover.
 *
 * `firstShiftEver` marks the one period where the sum-of-Z claim does not hold,
 * because money settled before the first shift's local day start is in no Z at
 * all. The screen says so rather than absorbing it.
 *
 * `custom` is which control is active, and `from`/`to` are the two dates the
 * manager TYPED — not `startsAt`/`endsAt`, which are the closes the range
 * happened to bracket. A picker that re-derived its fields from the shift
 * span would move the dates out from under whoever typed them.
 *
 * `truncated` says the window held more closes than `maxShifts` allows and
 * the server kept the most recent ones. `maxSpanDays` is the custom-range
 * ceiling `Staff::ReportPeriod::MAX_CUSTOM_SPAN_DAYS` enforces.
 * `latestSelectableDate` is today on the RESTAURANT's clock — the picker's
 * `max`, which can differ from the browser's own idea of today.
 */
export type ReportPeriodProps = {
  /** `Staff::ReportPeriod::CUSTOM` on a custom range, one of `REPORT_PRESETS` otherwise. */
  preset: ReportPreset | typeof CUSTOM_REPORT_PRESET
  shiftCount: number
  label: string
  empty: boolean
  startsAt: string | null
  endsAt: string | null
  firstShiftEver: boolean
  custom: boolean
  from: string | null
  to: string | null
  truncated: boolean
  maxShifts: number
  maxSpanDays: number
  latestSelectableDate: string
}

export type MethodTotal = {
  paymentMethod: string
  paymentMethodLabel: string
  count: number
  subtotal: string
  tip: string
  dinerFee: string
  /** Subtotal plus tip: what the payer handed over on this rail. */
  collected: string
}

/**
 * `date` is a bare `YYYY-MM-DD` already bucketed in the restaurant's zone — a
 * bucket label, not an instant. Re-timezoning it in the browser would move it.
 */
export type DayTotal = {
  date: string
  count: number
  subtotal: string
}

/** `hour` is 0-23 on the restaurant's wall clock, not the browser's. */
export type HourTotal = {
  hour: number
  count: number
  subtotal: string
}

/**
 * The previous period, reduced to what the comparison renders. Nothing on this
 * screen breaks last month down by hour, so the server does not send it.
 */
export type PreviousSales = {
  label: string
  transactionCount: number
  shiftCount: number
  subtotal: string
  averageTicket: string
  averagePerShift: string
}

/**
 * `Staff::ReportSerializer::SalesSerializer` — R1, and the only headline total
 * on the screen.
 *
 * The comparison is folded in rather than sitting beside it, because a period
 * total with nothing to measure it against is a number an owner cannot act on.
 *
 * Both ratios are `null` when the baseline is zero or absent — rendered as
 * "nuevo", never as +100%.
 *
 * `normalizeOnAverage` is the server saying the two sides bracket materially
 * different service counts (more than one shift apart), at which point a
 * total-vs-total delta is a calendar-length lie wearing a percentage sign. When
 * it is true the screen moves the delta onto `averagePerShift` and says so.
 */
export type PeriodSales = {
  transactionCount: number
  shiftCount: number
  subtotal: string
  tip: string
  dinerFee: string
  averageTicket: string
  averagePerShift: string
  busiestHour: HourTotal | null
  growthRatio: number | null
  perShiftGrowthRatio: number | null
  normalizeOnAverage: boolean
  byMethod: MethodTotal[]
  byDay: DayTotal[]
  byHour: HourTotal[]
  previous: PreviousSales | null
}

export type AbcClass = "a" | "b" | "c"

/**
 * `cost`, `margin` and `foodCostRatio` are `null` for a dish nobody costed, and
 * the screen renders that as an absence — never as `0.00`.
 *
 * A zero cost reads as infinite margin, which is the single most misleading
 * figure this screen could show, so the server sends nothing rather than a
 * number it cannot stand behind. `costedQuantity` says how many of `quantity`
 * the figure covers: a dish that is 3-of-40 costed must not read as a month's
 * margin.
 *
 * `foodCostRatio` is a ratio, so it is a `number` like `share` — already rounded
 * to four places server-side. No peso figure is derived from it here.
 */
export type MixItem = {
  locationItemId: number | null
  /** The frozen name off the order, so a deleted dish still reads. */
  name: string
  quantity: number
  abcClass: AbcClass
  share: number
  cumulativeShare: number
  gross: string
  net: string
  discount: string
  cost: string | null
  margin: string | null
  foodCostRatio: number | null
  costedQuantity: number
}

/**
 * `Staff::ReportSerializer::MixSerializer` — R2.
 *
 * Its money does NOT reconcile with `PeriodSales` and is never rendered as a
 * headline: it counts dishes, not payments, and a split share carries a subtotal
 * that belongs to no dish. See the query's own header.
 */
export type ProductMix = {
  itemCount: number
  quantity: number
  gross: string
  net: string
  /**
   * `null` for a restaurant that does not buy through Menny, and for one whose
   * dishes have no recipes yet. Both mean the same thing to this screen: there
   * is no cost column, and the block is exactly what #313 shipped.
   */
  cost: string | null
  margin: string | null
  foodCostRatio: number | null
  costedQuantity: number
  items: MixItem[]
}

/** `Staff::ControlReportQuery::ACTOR_BUCKETS`. */
export type ActorBucket = "person" | "system" | "unattributed"

/**
 * `label` is the actor's name when there is one and a server-resolved bucket
 * label otherwise. The two non-person buckets are not interchangeable: `system`
 * is normal housekeeping, `unattributed` is a finding.
 *
 * `amount` is `null` on a block that moves no money (reopenings), never zero:
 * zero would read as "nothing was at stake", the opposite of the point.
 */
export type ActorTotal = {
  bucket: ActorBucket
  count: number
  actor: string | null
  label: string
  amount: string | null
}

export type DiscountGroup = {
  kind: string
  kindLabel: string
  reason: string
  reasonLabel: string
  countsAsRevenue: boolean
  count: number
  amount: string
}

export type ControlDiscounts = {
  count: number
  revenueCount: number
  nonRevenueCount: number
  total: string
  revenueTotal: string
  nonRevenueTotal: string
  groups: DiscountGroup[]
  actors: ActorTotal[]
}

export type CancellationGroup = {
  scope: "bill" | "order"
  scopeLabel: string
  reason: string | null
  reasonLabel: string
  /** Housekeeping the machine did, with no actor and correctly so. */
  system: boolean
  count: number
  amount: string
}

export type ControlCancellations = {
  scope: "bill" | "order"
  count: number
  systemCount: number
  total: string
  systemTotal: string
  groups: CancellationGroup[]
  actors: ActorTotal[]
}

export type RefundGroup = {
  rail: "drawer" | "provider"
  reason: string | null
  reasonLabel: string
  count: number
  amount: string
}

export type ControlRefunds = {
  count: number
  drawerCount: number
  providerCount: number
  total: string
  drawerTotal: string
  providerTotal: string
  groups: RefundGroup[]
  actors: ActorTotal[]
}

export type ReopenGroup = {
  reason: string | null
  reasonLabel: string
  count: number
}

export type ControlReopenings = {
  count: number
  groups: ReopenGroup[]
  actors: ActorTotal[]
}

/**
 * `Staff::ReportSerializer::ControlSerializer` — R3, the differentiator.
 *
 * **Nothing in here is subtracted from sales anywhere on this screen.** Sales
 * are already net of discounts, and no Z subtracts a refund. This report names
 * that money; it never nets it.
 */
export type ControlReport = {
  discounts: ControlDiscounts
  billCancellations: ControlCancellations
  orderCancellations: ControlCancellations
  refunds: ControlRefunds
  reopenings: ControlReopenings
}

export type CapturePerson = {
  name: string | null
  role: string | null
  roleLabel: string | null
  /** Left the restaurant, but still captured these orders at a till. */
  former: boolean
  orderCount: number
  itemCount: number
  amount: string
}

/**
 * One row, never a list. Naming individual diners would put the restaurant's
 * customers on a staff performance screen.
 */
export type SelfServiceCapture = {
  dinerCount: number
  orderCount: number
  itemCount: number
  label: string
  amount: string
}

/** `Staff::ReportSerializer::CaptureSerializer` — "Capturado por", not "por mesero". */
export type CaptureReport = {
  orderCount: number
  itemCount: number
  total: string
  people: CapturePerson[]
  selfService: SelfServiceCapture
}

// ---------------------------------------------------------------- forecast

/**
 * `Staff::Forecast::Horizon::LENGTHS` — six options, two stored fits.
 * 7/14/30 are prefixes of the stored 30-day fit; 4/8/12 of the stored 12-week
 * one. `Staff::Forecast::Horizon.parse`'s own pattern is what makes this string
 * survive a URL param, a TS union member and a `data-testid` suffix unquoted.
 */
export const FORECAST_HORIZON_KEYS = [
  "day-7",
  "day-14",
  "day-30",
  "week-4",
  "week-8",
  "week-12",
] as const

export type ForecastHorizonKey = (typeof FORECAST_HORIZON_KEYS)[number]

/** `Staff::Forecast::Horizon::DEFAULT_KEY`. The server falls back to this too. */
export const DEFAULT_FORECAST_HORIZON: ForecastHorizonKey = "day-7"

export type ForecastHorizonUnit = "day" | "week"

/**
 * One chip. `label` is resolved server-side through `ui.staff.reports.forecast
 * .horizons.*` — see that class's header for why the label sits beside the
 * PRESETS list resolving the exact same way two lines above it in the
 * controller. `enabled` is gated on ONE number (the longer of the two stored
 * fits' `historyDays`), not per-unit, so a horizon whose own fit failed
 * transiently does not grey out just because its sibling unit succeeded.
 */
export type ForecastHorizonOption = {
  value: ForecastHorizonKey
  unit: ForecastHorizonUnit
  length: number
  label: string
  testId: string
  enabled: boolean
}

/** `Location::SalesForecast::FORECAST_METHODS`. */
export type ForecastMethod = "prophet" | "weekday_seasonal"

/**
 * One day or one week of the model's own output. Money arrives as an exact
 * decimal string, precisely like every other figure on this screen — Prophet's
 * or the weekday adapter's float output was already rounded to integer cents and
 * rendered through `Staff::ReportSerializer.money` before it left Ruby, so
 * nothing here may re-derive it through a plotting float.
 */
export type ForecastPoint = {
  date: string
  mean: string
  lower: string
  upper: string
}

/**
 * `Staff::Forecast::ReportBlock#result_hash` — five states, not the nine the
 * underlying `pm-po` union suggested, because the screen cannot act on some of
 * the finer distinctions (see that class's header). `result` and `pace` are
 * SIBLINGS on `ForecastProps`, not two branches of one union: they answer
 * different questions and an owner wants both at once.
 *
 * `stale` carries no `points` AT ALL, on purpose — a stale forecast drawn under
 * a badge is still a stale forecast drawn as if it were today's, which is
 * exactly what the AC forbids. Never reach for a cached `ready` result to paper
 * over a `stale` one.
 */
export type ForecastResult =
  | {
      state: "ready"
      forecastMethod: ForecastMethod
      unit: ForecastHorizonUnit
      asOf: string
      historyDays: number
      historyShiftCount: number
      /** 0..1 — e.g. `0.8` for an 80% band. A ratio, so a `number` like `share`. */
      intervalWidth: number | null
      points: ForecastPoint[]
    }
  | { state: "stale"; asOf: string; staleByDays: number }
  | { state: "failed"; asOf: string; attempts: number; failedAt: string | null; exhausted: boolean }
  | { state: "pending" }
  | { state: "unavailable" }

/**
 * `Staff::Forecast::PaceProjection` — at the CURRENT rate, where the period
 * being viewed lands by its own end. `null` for a finished window, for
 * `last_30_shifts`, and for a period with no close — the same three conditions
 * `PaceProjection#call` itself returns `nil` for, not re-derived here.
 *
 * Projected on SERVICE days, not calendar days: a Monday-closed restaurant
 * fourteen calendar days into a period has worked twelve services, not
 * fourteen.
 */
export type PaceProjection = {
  subtotal: string
  projectedSubtotal: string
  serviceDays: number
  projectedServiceDays: number
  remainingCalendarDays: number
} | null

/**
 * `Staff::Forecast::ReportBlock#call` — the whole forecast block, nested inside
 * `report` rather than beside it because that hash is exactly what
 * `Staff::ReportCsvService` renders and the export gains forecast rows from the
 * same field.
 */
export type ForecastProps = {
  horizon: ForecastHorizonKey
  horizons: ForecastHorizonOption[]
  result: ForecastResult
  pace: PaceProjection
  /**
   * Whether the selected window runs up to today. `false` means the period's own
   * `byDay` and this forecast do NOT share an axis — see
   * `Staff::Forecast::ReportBlock#call`, which owns the rule.
   */
  periodReachesToday: boolean
  /** The Rappi/UberEats gap, in words — the same sentence `mix`'s note carries. */
  coverageNote: string
}

/** The whole payload: `Staff::ReportSerializer.render_as_hash`. */
export type ReportPayload = {
  timeZone: string
  period: ReportPeriodProps
  sales: PeriodSales
  mix: ProductMix
  control: ControlReport
  capture: CaptureReport
  forecast: ForecastProps
}

export type ReportsPageProps = {
  report: ReportPayload
  /** The restaurant's zone. Every timestamp on this screen renders in it. */
  timeZone: string
  /**
   * The active preset, echoed back after the server's own whitelist —
   * `Staff::ReportPeriod::CUSTOM` on a custom range, so this is not narrowed
   * to `ReportPreset`. `report.period` carries the full period shape; this
   * top-level echo exists for callers that only need the slug.
   */
  period: ReportPreset | typeof CUSTOM_REPORT_PRESET
  /** `PRESETS` only — never includes `custom`. See `CUSTOM_REPORT_PRESET`'s header. */
  periods: ReportPresetOption[]
  /**
   * Fully formed by the server, including a custom range's `from`/`to` — see
   * `Staff::ReportPeriod#url_params`. The screen must not append `?period=`
   * to this itself: that was fine while a window was one param and became a
   * place for the screen and the file to disagree the moment it was three.
   */
  exportPath: string
  /**
   * Whether this location has EVER closed a shift.
   *
   * It separates the two empties, which need different sentences: a restaurant
   * that has never cut a till has no report at any preset, while one that simply
   * has not closed today's shift yet should be told to look at another period.
   *
   * Optional, and it defaults to `true`, so if the controller does not send it
   * the screen falls back to the softer always-true message rather than telling
   * an established restaurant it has never opened.
   */
  hasClosedShifts?: boolean
}
